import type { SaveProjectConfigData } from 'types/apis/pilotInterfaces';
import { isDuplicateConfig } from './registryUtils';

/**
 * Stable key for matching registry rows across the "user/METADATA batch" and "default values" batch saves.
 */
export function makeProjectConfigRegistryKey(configType: string, configKey: string): string {
  return `${configType}|${configKey}`;
}

/**
 * Collects all `config_type|config_key` pairs from a first save payload (e.g. `allToSave`).
 */
export function collectKeysFromSavePayload(
  rows: Pick<SaveProjectConfigData, 'config_type' | 'config_key'>[]
): Set<string> {
  const set = new Set<string>();
  for (const row of rows) {
    set.add(makeProjectConfigRegistryKey(row.config_type, row.config_key));
  }
  return set;
}

/**
 * When true, skip saving a registry default for this row — the same key was already persisted
 * in the first batch (prevents overwriting user input / METADATA with `defaultValue`).
 */
export function shouldSkipDefaultConfigDuplicate(
  firstBatchKeys: Set<string>,
  configType: string,
  configKey: string
): boolean {
  return firstBatchKeys.has(makeProjectConfigRegistryKey(configType, configKey));
}

/** File types whose registry defaults get persisted on project creation. */
export const DEFAULT_CONFIG_ALLOWED_FILE_TYPES: readonly string[] = [
  'ALL',
  'USER_META',
  'METER',
  'CONSUMPTION',
  'BC_SCHEDULE',
  'BILLING',
];

export type DefaultConfigDropReason =
  | 'noDefaultValue'
  | 'isDuplicate'
  | 'badFileType'
  | 'alreadyInFirstBatch';

/**
 * Why a registry config is excluded from the defaults save batch, or null when it is included.
 * Checks run in the same order the save flow applies them, so a drop is attributable to one cause.
 */
export function classifyDefaultConfig(
  config: any,
  configType: string,
  firstBatchKeys: Set<string>,
  allowedFileTypes: readonly string[] = DEFAULT_CONFIG_ALLOWED_FILE_TYPES
): DefaultConfigDropReason | null {
  const hasDefaultValue =
    !!config?.value &&
    (typeof config.value === 'string' ? config.value.trim() !== '' : true);
  if (!hasDefaultValue) return 'noDefaultValue';

  if (isDuplicateConfig(config)) return 'isDuplicate';

  // An absent FILE_TYPE means "not file-specific", so it stays in; a present-but-unlisted one is dropped.
  const fileType = typeof config.FILE_TYPE === 'string' ? config.FILE_TYPE.trim() : '';
  if (fileType && !allowedFileTypes.includes(fileType)) return 'badFileType';

  if (shouldSkipDefaultConfigDuplicate(firstBatchKeys, configType, config.configKey)) {
    return 'alreadyInFirstBatch';
  }

  return null;
}
